import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Database, Plus, Upload, Trash2, FileSpreadsheet, FolderPlus } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { STATUS_CONFIG, formatDate, SOURCE_CONFIG } from '../utils/constants';
import { useAuth } from '../context/AuthContext';

export default function Datasets() {
  const { user } = useAuth();
  const [datasets, setDatasets] = useState([]);
  const [showImport, setShowImport] = useState(false);
  const [showCSV, setShowCSV] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [importData, setImportData] = useState({ dataset_name: '', apify_dataset_id: '', field_mapping: {} });
  const [csvData, setCsvData] = useState({ dataset_name: '', data: [], fields: [], field_mapping: {} });
  const [createData, setCreateData] = useState({ name: '', description: '' });
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get('/datasets').then(res => setDatasets(res.data));
  useEffect(() => { load(); }, []);

  // Apify import handlers
  const handlePreview = async () => {
    if (!importData.apify_dataset_id) return toast.error('Ingresá el Dataset ID de Apify');
    try {
      const res = await api.post('/datasets/preview-apify', { apify_dataset_id: importData.apify_dataset_id });
      setPreview(res.data);
      toast.success(`${res.data.fields.length} campos encontrados`);
    } catch (err) { toast.error(err.response?.data?.error || 'Error al previsualizar'); }
  };

  const handleImport = async () => {
    if (!importData.dataset_name) return toast.error('Ponele un nombre a la base');
    setImporting(true);
    try {
      const res = await api.post('/datasets/import-apify', importData);
      toast.success(res.data.message);
      setShowImport(false); setImportData({ dataset_name: '', apify_dataset_id: '', field_mapping: {} }); setPreview(null);
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Error al importar'); }
    finally { setImporting(false); }
  };

  // CSV import
  const handleCSVFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      const lines = text.split('\n').filter(l => l.trim());
      if (lines.length < 2) return toast.error('El archivo está vacío');
      const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim());
      const data = lines.slice(1).map(line => {
        const values = line.match(/(".*?"|[^",\s]+)(?=\s*,|\s*$)/g) || line.split(',');
        const obj = {};
        headers.forEach((h, i) => { obj[h] = (values[i] || '').replace(/^"|"$/g, '').trim(); });
        return obj;
      });
      setCsvData({ ...csvData, data, fields: headers, dataset_name: file.name.replace('.csv','') });
      toast.success(`${data.length} registros encontrados`);
    };
    reader.readAsText(file, 'UTF-8');
  };

  const handleCSVImport = async () => {
    if (!csvData.dataset_name) return toast.error('Ponele un nombre a la base');
    setImporting(true);
    try {
      const res = await api.post('/datasets/import-csv', { dataset_name: csvData.dataset_name, csv_data: csvData.data, field_mapping: csvData.field_mapping });
      toast.success(res.data.message);
      setShowCSV(false); setCsvData({ dataset_name: '', data: [], fields: [], field_mapping: {} });
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Error al importar'); }
    finally { setImporting(false); }
  };

  // Create empty
  const handleCreate = async () => {
    if (!createData.name) return toast.error('Ponele un nombre');
    try {
      await api.post('/datasets', createData);
      toast.success('Base creada');
      setShowCreate(false); setCreateData({ name: '', description: '' });
      load();
    } catch (err) { toast.error('Error al crear'); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`¿Eliminar "${name}" y todos sus leads?`)) return;
    try { await api.delete(`/datasets/${id}`); toast.success('Base eliminada'); load(); }
    catch (err) { toast.error('Error al eliminar'); }
  };

  const statusBadges = (ds) => {
    const statuses = ['frio','contactado','en_conversacion','llamada_agendada','propuesta_enviada','ganado','perdido','no_responde'];
    const counts = { frio: ds.frio_count, contactado: ds.contactado_count, en_conversacion: ds.conversacion_count,
      llamada_agendada: ds.llamada_count, propuesta_enviada: ds.propuesta_count, ganado: ds.ganado_count,
      perdido: ds.perdido_count, no_responde: ds.no_responde_count };
    return statuses.filter(s => counts[s] > 0).map(s => (
      <span key={s} className="badge" style={{ background: STATUS_CONFIG[s].bg, color: STATUS_CONFIG[s].color }}>{counts[s]} {STATUS_CONFIG[s].label}</span>
    ));
  };

  const mappingFields = ['business_name', 'phone', 'email', 'website', 'address', 'city', 'category'];
  const mappingLabels = { business_name: 'Nombre del negocio', phone: 'Teléfono', email: 'Email', website: 'Sitio web', address: 'Dirección', city: 'Ciudad', category: 'Sector' };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 10 }}>
        <h1 style={{ fontSize: 26, fontWeight: 800 }}>Mis Bases de Datos</h1>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary btn-sm" onClick={() => setShowCreate(true)}><FolderPlus size={14} /> Crear vacía</button>
          <button className="btn btn-secondary btn-sm" onClick={() => setShowCSV(true)}><FileSpreadsheet size={14} /> Importar CSV</button>
          {user?.role !== "client" && <button className="btn btn-primary btn-sm" onClick={() => setShowImport(true)}><Upload size={14} /> Research</button>}
        </div>
      </div>

      {datasets.length === 0 ? (
        <div className="empty-state">
          <Database size={48} /><h3>No hay bases importadas</h3><p>Importá tu primer dataset para empezar</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
          {datasets.map(ds => (
            <div key={ds.id} className="card" style={{ cursor: 'pointer', transition: 'border-color 0.2s' }}
              onClick={() => navigate(`/datasets/${ds.id}`)}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                <div>
                  <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>{ds.name}</h3>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>{formatDate(ds.created_at)}</p>
                    {ds.source_type && (
                      <span className="badge" style={{ background: SOURCE_CONFIG[ds.source_type]?.bg || 'rgba(100,116,139,0.15)', color: SOURCE_CONFIG[ds.source_type]?.color || '#64748b', fontSize: 10 }}>
                        {SOURCE_CONFIG[ds.source_type]?.label || ds.source_type}
                      </span>
                    )}
                  </div>
                </div>
                <button className="btn btn-ghost" style={{ padding: 6 }} onClick={(e) => { e.stopPropagation(); handleDelete(ds.id, ds.name); }}>
                  <Trash2 size={14} color="var(--danger)" />
                </button>
              </div>
              <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--accent)', marginBottom: 12 }}>
                {ds.lead_count} <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-secondary)' }}>leads</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{statusBadges(ds)}</div>
            </div>
          ))}
        </div>
      )}

      {/* Apify Import Modal */}
      {showImport && (
        <div className="modal-overlay" onClick={() => { setShowImport(false); setPreview(null); }}>
          <div className="modal" style={{ maxWidth: 640 }} onClick={e => e.stopPropagation()}>
            <h2><Upload size={20} style={{ marginRight: 8, verticalAlign: 'middle' }} />Importar desde Research</h2>
            <div className="form-group">
              <label>Nombre para esta base</label>
              <input value={importData.dataset_name} onChange={e => setImportData({...importData, dataset_name: e.target.value})} placeholder="Ej: Inmobiliarias CABA - Agosto 2026" />
            </div>
            <div className="form-group">
              <label>Dataset ID de Apify</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input value={importData.apify_dataset_id} onChange={e => setImportData({...importData, apify_dataset_id: e.target.value})} placeholder="Ej: abc123XYZ" style={{ flex: 1 }} />
                <button className="btn btn-secondary btn-sm" onClick={handlePreview}>Previsualizar</button>
              </div>
            </div>
            {preview && (
              <div style={{ marginTop: 16 }}>
                <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: 'var(--accent)' }}>Mapeo de campos</h4>
                {mappingFields.map(field => (
                  <div key={field} className="form-group" style={{ marginBottom: 10 }}>
                    <label style={{ fontSize: 12 }}>{mappingLabels[field]}</label>
                    <select value={importData.field_mapping[field] || ''} onChange={e => setImportData({...importData, field_mapping: {...importData.field_mapping, [field]: e.target.value}})}>
                      <option value="">Auto-detectar</option>
                      {preview.fields.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button className="btn btn-secondary" onClick={() => { setShowImport(false); setPreview(null); }}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleImport} disabled={importing}>{importing ? 'Importando...' : 'Importar leads'}</button>
            </div>
          </div>
        </div>
      )}

      {/* CSV Import Modal */}
      {showCSV && (
        <div className="modal-overlay" onClick={() => setShowCSV(false)}>
          <div className="modal" style={{ maxWidth: 640 }} onClick={e => e.stopPropagation()}>
            <h2><FileSpreadsheet size={20} style={{ marginRight: 8, verticalAlign: 'middle' }} />Importar desde CSV</h2>
            <div className="form-group">
              <label>Archivo CSV</label>
              <input type="file" accept=".csv" onChange={handleCSVFile} style={{ padding: 10 }} />
            </div>
            {csvData.fields.length > 0 && (
              <>
                <div className="form-group">
                  <label>Nombre para esta base</label>
                  <input value={csvData.dataset_name} onChange={e => setCsvData({...csvData, dataset_name: e.target.value})} />
                </div>
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>{csvData.data.length} registros detectados</p>
                <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: 'var(--accent)' }}>Mapeo de campos</h4>
                {mappingFields.map(field => (
                  <div key={field} className="form-group" style={{ marginBottom: 10 }}>
                    <label style={{ fontSize: 12 }}>{mappingLabels[field]}</label>
                    <select value={csvData.field_mapping[field] || ''} onChange={e => setCsvData({...csvData, field_mapping: {...csvData.field_mapping, [field]: e.target.value}})}>
                      <option value="">Auto-detectar</option>
                      {csvData.fields.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                ))}
              </>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button className="btn btn-secondary" onClick={() => setShowCSV(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleCSVImport} disabled={importing || csvData.data.length === 0}>
                {importing ? 'Importando...' : 'Importar leads'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Empty Modal */}
      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <h2><FolderPlus size={20} style={{ marginRight: 8, verticalAlign: 'middle' }} />Crear base vacía</h2>
            <div className="form-group">
              <label>Nombre</label>
              <input value={createData.name} onChange={e => setCreateData({...createData, name: e.target.value})} placeholder="Ej: Leads de eventos" />
            </div>
            <div className="form-group">
              <label>Descripción (opcional)</label>
              <textarea value={createData.description} onChange={e => setCreateData({...createData, description: e.target.value})} placeholder="Describí el propósito de esta base" rows={3} style={{ resize: 'vertical' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
              <button className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleCreate}>Crear base</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
